import React, { useMemo, useState } from 'react';
import {
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaPhoneAlt,
  FaWhatsapp,
} from 'react-icons/fa';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  company: '',
  projectType: '',
  budget: '',
  timeline: '',
  description: '',
};

const projectTypes = [
  'Website',
  'Web Application',
  'Mobile Application',
  'E-commerce',
  'Backend / API',
  'SaaS',
  'Existing Application / Bug Fix',
  'Other',
];

const budgetRanges = [
  'Not sure yet',
  'Small project',
  'Medium project',
  'Large project',
  'Ongoing support / maintenance',
];

const timelineOptions = [
  'Not sure yet',
  'ASAP',
  '2-4 weeks',
  '1-2 months',
  '3+ months',
  'Ongoing',
];

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function buildMailto(profile, form) {
  const subject = encodeURIComponent(`Project Inquiry from ${form.name}`);
  const body = encodeURIComponent([
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    `Phone / WhatsApp: ${form.phone}`,
    `Company: ${form.company || 'Not provided'}`,
    `Project Type: ${form.projectType}`,
    `Estimated Budget: ${form.budget}`,
    `Expected Timeline: ${form.timeline}`,
    '',
    'Project Description:',
    form.description,
  ].join('\n'));

  return `mailto:${profile.email}?subject=${subject}&body=${body}`;
}

async function submitProjectInquiry(profile, form, mailtoHref) {
  const endpoint = process.env.REACT_APP_CONTACT_ENDPOINT;

  if (endpoint) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...form,
        recipient: profile.email,
        source: 'portfolio-project-inquiry',
      }),
    });

    if (!response.ok) throw new Error('Project inquiry submission failed.');
    return 'api';
  }

  const mailLink = document.createElement('a');
  mailLink.href = mailtoHref;
  mailLink.click();
  return 'email';
}

function Contact({ profile }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  const whatsappHref = useMemo(() => {
    const phone = profile.phone?.replace(/[^\d]/g, '');
    if (!phone) return '';
    return `https://wa.me/${phone}`;
  }, [profile.phone]);
  const telHref = useMemo(() => `tel:${profile.phone?.replace(/\s/g, '')}`, [profile.phone]);

  const mailtoHref = useMemo(() => buildMailto(profile, form), [profile, form]);

  const validateForm = () => {
    const nextErrors = {};

    if (!form.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!form.email.trim()) {
      nextErrors.email = 'Please enter your email.';
    } else if (!validateEmail(form.email)) {
      nextErrors.email = 'Please enter a valid email address.';
    }
    if (!form.phone.trim()) nextErrors.phone = 'Please enter your phone or WhatsApp number.';
    if (!form.projectType) nextErrors.projectType = 'Please select a project type.';
    if (!form.budget) nextErrors.budget = 'Please select an estimated budget.';
    if (!form.timeline) nextErrors.timeline = 'Please select an expected timeline.';
    if (!form.description.trim()) {
      nextErrors.description = 'Please describe the project.';
    } else if (form.description.trim().length < 20) {
      nextErrors.description = 'Please add a little more detail about the project.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
    if (status !== 'idle') setStatus('idle');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      const result = await submitProjectInquiry(profile, form, mailtoHref);
      setStatus('success');
      if (result === 'api') setForm(initialForm);
    } catch (error) {
      setStatus('submit-error');
    }
  };

  const fieldProps = (name) => ({
    name,
    value: form[name],
    onChange: updateField,
    'aria-invalid': errors[name] ? 'true' : 'false',
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
  });

  return (
    <section className="portfolioContact sectionReveal" id="contact">
      <div className="portfolioContact__intro">
        <span className="portfolioEyebrow">Project Inquiry</span>
        <h2>Have a Project in Mind?</h2>
        <p>Tell me what you're building, and let's discuss how I can help turn your idea into a working product.</p>

        <div className="portfolioContact__grid">
          <a href={`mailto:${profile.email}`}><FaEnvelope /> {profile.email}</a>
          <a href={telHref}><FaPhoneAlt /> {profile.phone}</a>
          {whatsappHref && <a href={whatsappHref} target="_blank" rel="noreferrer"><FaWhatsapp /> WhatsApp</a>}
          <a href={profile.linkedIn} target="_blank" rel="noreferrer"><FaLinkedin /> LinkedIn</a>
          <a href={profile.github} target="_blank" rel="noreferrer"><FaGithub /> GitHub</a>
          <span><FaMapMarkerAlt /> {profile.location}</span>
        </div>
      </div>

      <form className="portfolioInquiryForm" onSubmit={handleSubmit} noValidate>
        <div className="portfolioFormGrid">
          <label>
            <span>Name *</span>
            <input type="text" autoComplete="name" {...fieldProps('name')} />
            {errors.name && <small id="name-error">{errors.name}</small>}
          </label>

          <label>
            <span>Email *</span>
            <input type="email" autoComplete="email" {...fieldProps('email')} />
            {errors.email && <small id="email-error">{errors.email}</small>}
          </label>

          <label>
            <span>Phone / WhatsApp *</span>
            <input type="tel" autoComplete="tel" {...fieldProps('phone')} />
            {errors.phone && <small id="phone-error">{errors.phone}</small>}
          </label>

          <label>
            <span>Company</span>
            <input type="text" autoComplete="organization" {...fieldProps('company')} />
          </label>

          <label>
            <span>Project Type *</span>
            <select {...fieldProps('projectType')}>
              <option value="">Select project type</option>
              {projectTypes.map((type) => <option value={type} key={type}>{type}</option>)}
            </select>
            {errors.projectType && <small id="projectType-error">{errors.projectType}</small>}
          </label>

          <label>
            <span>Estimated Budget *</span>
            <select {...fieldProps('budget')}>
              <option value="">Select budget range</option>
              {budgetRanges.map((budget) => <option value={budget} key={budget}>{budget}</option>)}
            </select>
            {errors.budget && <small id="budget-error">{errors.budget}</small>}
          </label>

          <label>
            <span>Expected Timeline *</span>
            <select {...fieldProps('timeline')}>
              <option value="">Select timeline</option>
              {timelineOptions.map((timeline) => <option value={timeline} key={timeline}>{timeline}</option>)}
            </select>
            {errors.timeline && <small id="timeline-error">{errors.timeline}</small>}
          </label>

          <label className="portfolioFormGrid__full">
            <span>Project Description *</span>
            <textarea rows="5" {...fieldProps('description')} />
            {errors.description && <small id="description-error">{errors.description}</small>}
          </label>
        </div>

        {status === 'success' && (
          <p className="portfolioFormStatus portfolioFormStatus--success">
            Your email app should open with the inquiry details. Send it from there to complete the inquiry.
          </p>
        )}
        {status === 'error' && (
          <p className="portfolioFormStatus portfolioFormStatus--error">
            Please fix the highlighted fields and try again.
          </p>
        )}
        {status === 'submit-error' && (
          <p className="portfolioFormStatus portfolioFormStatus--error">
            Something went wrong while sending the inquiry. Please email me directly or try again.
          </p>
        )}

        <button type="submit" className="portfolioButton portfolioButton--primary" disabled={status === 'loading'}>
          <FaPaperPlane />
          {status === 'loading' ? 'Preparing Inquiry...' : 'Send Project Inquiry'}
        </button>
      </form>
    </section>
  );
}

export default Contact;
